import React, { useEffect, useRef, useState } from 'react';
import { places } from './places';
import { weatherLabel } from './weather';

export default function MapCanvas({
  assetBase,
  selected,
  focusRequest,
  category,
  hunt,
  found,
  visibleIds,
  route,
  onSelect,
  onFind,
  onStatus,
  onProgress,
  onLoadPhase,
}) {
  const canvas = useRef(null);
  const controller = useRef(null);
  const buttons = useRef({});
  const lines = useRef({});
  const north = useRef(null);
  const state = useRef({ selected, category, hunt, found, visibleIds, route, focusRequest, onStatus });
  state.current = { selected, category, hunt, found, visibleIds, route, focusRequest, onStatus };

  const [status, setStatus] = useState('loading');
  const [progress, setProgress] = useState(0);
  const [retry, setRetry] = useState(0);
  const [view, setView] = useState('3d');
  const [orbit, setOrbit] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [living, setLiving] = useState(true);
  const [weather, setWeather] = useState({ data: null, state: 'loading' });
  const [weatherEnabled, setWeatherEnabled] = useState(true);
  const [drive, setDrive] = useState({ away: false, busy: false, message: 'The Duke is home' });
  const weatherEnabledRef = useRef(weatherEnabled);
  weatherEnabledRef.current = weatherEnabled;
  const livingRef = useRef(living);
  livingRef.current = living;
  const ready = status === 'ready';

  const onFrame = useRef((points, bearing) => {
    if (north.current) north.current.style.transform = `rotate(${bearing}deg)`;
    const s = state.current;
    const placed = [];
    const sorted = [...points].sort((a, b) => Number(b.id === s.selected) - Number(a.id === s.selected));
    for (const p of sorted) {
      const button = buttons.current[p.id];
      const line = lines.current[p.id];
      const place = places.find((q) => q.id === p.id);
      if (!button || !line || (!place && p.id !== 'tree-surgeon')) continue;
      let visible =
        p.visible &&
        (s.visibleIds.includes(p.id) ||
          (p.id === 'tree-surgeon' && !s.hunt && (s.category === 'all' || s.category === 'gardens')));
      let x = p.x;
      let y = p.y;
      if (visible) {
        const offsets = [[0, 0], [0, -48], [48, 0], [-48, 0], [0, 48], [48, -48], [-48, -48]];
        const w = canvas.current?.clientWidth || 0;
        const h = canvas.current?.clientHeight || 0;
        const candidate = offsets.find(([dx, dy]) => {
          const nx = p.x + dx;
          const ny = p.y + dy;
          return nx > 24 && ny > 70 && nx < w - 24 && ny < h - 70 && !placed.some((q) => Math.abs(q.x - nx) < 44 && Math.abs(q.y - ny) < 44);
        });
        if (candidate) {
          x += candidate[0];
          y += candidate[1];
          placed.push({ x, y });
        } else visible = false;
      }
      button.style.visibility = visible ? 'visible' : 'hidden';
      button.style.left = `${x}px`;
      button.style.top = `${y}px`;
      line.style.visibility = visible ? 'visible' : 'hidden';
      line.setAttribute('x1', String(p.x));
      line.setAttribute('y1', String(p.y));
      line.setAttribute('x2', String(x));
      line.setAttribute('y2', String(y));
    }
  });

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const change = () => {
      setReduced(media.matches);
      if (media.matches) setLiving(false);
    };
    change();
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);

  useEffect(() => {
    const abort = new AbortController();
    let stopped = false;
    setStatus('loading');
    setProgress(0);
    onStatus('Loading the 3D landscape');
    onLoadPhase?.('loading');
    onProgress?.(0);

    import('./renderer')
      .then(({ createRenderer }) => {
        if (stopped || !canvas.current) return;
        try {
          controller.current = createRenderer({
            onDrive: (s) => {
              if (!stopped) setDrive(s);
            },
            onWeather: (s) => {
              if (!stopped) setWeather(s);
            },
            canvas: canvas.current,
            assetBase,
            signal: abort.signal,
            onFrame: (p, b) => onFrame.current(p, b),
            onInteraction: () => {
              setOrbit(false);
              setView('3d');
            },
            onReady: () => {
              if (!stopped) {
                setStatus('ready');
                setProgress(100);
                state.current.onStatus('3D map ready');
                onProgress?.(100);
                onLoadPhase?.('ready');
                controller.current?.animate(livingRef.current);
                controller.current?.weather(weatherEnabledRef.current);
                controller.current?.route(state.current.route);
                controller.current?.setActive(state.current.selected);
                if (state.current.focusRequest > 0) controller.current?.focus(state.current.selected);
              }
            },
            onProgress: (p) => {
              if (!stopped) {
                setProgress(p);
                onProgress?.(p);
              }
            },
            onError: () => {
              if (!stopped) {
                setStatus('error');
                state.current.onStatus('3D view unavailable. All places are available in the list.');
                onLoadPhase?.('error');
              }
            },
          });
        } catch {
          setStatus('error');
          state.current.onStatus('3D view unavailable. All places are available in the list.');
          onLoadPhase?.('error');
        }
      })
      .catch(() => {
        if (!stopped) {
          setStatus('error');
          onLoadPhase?.('error');
        }
      });

    return () => {
      stopped = true;
      abort.abort();
      controller.current?.destroy();
      controller.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetBase, retry]);

  useEffect(() => {
    controller.current?.setActive(selected);
    if (focusRequest > 0) {
      controller.current?.orbit(false);
      setOrbit(false);
      controller.current?.focus(selected);
      setView('3d');
    }
  }, [selected, focusRequest]);

  useEffect(() => {
    controller.current?.setActive(selected);
  }, [category, hunt, found, selected]);

  useEffect(() => {
    controller.current?.route(route);
  }, [route]);

  useEffect(() => {
    controller.current?.setActive(selected);
  }, [visibleIds, selected]);

  function key(e) {
    if (!ready) return;
    const c = controller.current;
    if (!c) return;
    let used = true;
    switch (e.key) {
      case 'ArrowLeft':
        c.rotate(-0.15, 0);
        break;
      case 'ArrowRight':
        c.rotate(0.15, 0);
        break;
      case 'ArrowUp':
        c.rotate(0, -0.12);
        break;
      case 'ArrowDown':
        c.rotate(0, 0.12);
        break;
      case '+':
      case '=':
        c.zoom(1.2);
        break;
      case '-':
        c.zoom(1 / 1.2);
        break;
      case 'Home':
        c.home();
        setView('3d');
        break;
      default:
        used = false;
    }
    if (used) e.preventDefault();
  }

  const weatherTime = weather.data
    ? new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit' }).format(weather.data.time)
    : '';

  return (
    <>
      <div className="be-map-stage">
      {!ready && (
        <img
          className="be-overview be-fallback"
          src={`${assetBase}/belvoir-overview.jpg`}
          alt="Overview of Belvoir Castle and its surrounding garden paths"
        />
      )}
      <canvas
        ref={canvas}
        className={`be-canvas${ready ? ' is-ready' : ''}`}
        tabIndex={ready ? 0 : -1}
        aria-label="Interactive garden map. Arrow keys rotate and tilt, plus and minus zoom, Home resets."
        onKeyDown={key}
      />
      {ready && (
        <>
          <svg className="be-marker-lines" aria-hidden="true">
            <line
              ref={(el) => {
                lines.current['tree-surgeon'] = el;
              }}
            />
            {places.map((p) => (
              <line
                key={p.id}
                ref={(el) => {
                  lines.current[p.id] = el;
                }}
              />
            ))}
          </svg>
          <div className="be-markers">
            <button
              type="button"
              ref={(el) => {
                buttons.current['tree-surgeon'] = el;
              }}
              className="be-marker"
              aria-label="Zoom in on the tree surgeon"
              onClick={() => {
                controller.current?.focus('tree-surgeon');
                setOrbit(false);
                setView('3d');
              }}
            >
              <span aria-hidden="true">🪵</span>
              <span className="be-marker-name">Tree surgeon · zoom in</span>
            </button>
            {places.map((p) => (
              <button
                type="button"
                key={p.id}
                ref={(el) => {
                  buttons.current[p.id] = el;
                }}
                className={`be-marker${p.id === selected ? ' is-active' : ''}${hunt && p.bee ? ' is-bee' : ''}`}
                aria-label={
                  hunt && p.bee
                    ? `${found.includes(p.id) ? 'Bee found at ' : 'Find the bee at '}${p.name}`
                    : p.name
                }
                aria-pressed={p.id === selected}
                onClick={() => {
                  onSelect(p.id);
                  if (hunt && p.bee) onFind(p.id);
                }}
              >
                {hunt && p.bee ? (found.includes(p.id) ? '✓' : '🐝') : p.number}
                <span className="be-marker-name">{p.name}</span>
              </button>
            ))}
          </div>
        </>
      )}

      <div className="be-view-controls" aria-label="Map view">
        <button
          type="button"
          className={view === '3d' ? 'is-on' : ''}
          disabled={!ready}
          onClick={() => {
            controller.current?.home();
            setView('3d');
          }}
        >
          3D
        </button>
        <button
          type="button"
          className={view === 'top' ? 'is-on' : ''}
          disabled={!ready}
          onClick={() => {
            controller.current?.top();
            setView('top');
          }}
        >
          Top view
        </button>
        <button
          type="button"
          className={orbit ? 'is-on' : ''}
          disabled={!ready || reduced}
          aria-pressed={orbit}
          onClick={() => setOrbit(controller.current?.orbit(!orbit) || false)}
        >
          {orbit ? 'Pause' : 'Orbit'}
        </button>
      </div>

      {ready && (
        <div className="be-compass" aria-label="North">
          <span ref={north}>↑</span>
          <b>N</b>
        </div>
      )}

      <div className="be-zoom" aria-label="Map controls">
        <button type="button" aria-label="Zoom in" disabled={!ready} onClick={() => controller.current?.zoom(1.25)}>
          +
        </button>
        <button type="button" aria-label="Zoom out" disabled={!ready} onClick={() => controller.current?.zoom(0.8)}>
          −
        </button>
        <button
          type="button"
          aria-label="Reset map view"
          disabled={!ready}
          onClick={() => {
            controller.current?.home();
            setView('3d');
          }}
        >
          ↺
        </button>
      </div>

      {status === 'loading' && (
        <div className="be-load" role="status">
          <span className="sr-only">Opening the gardens{progress > 0 ? ` · ${progress}%` : ''}</span>
        </div>
      )}
      {status === 'error' && (
        <div className="be-load be-error" role="status">
          <strong>The 3D view couldn’t open.</strong>
          <p>You can still explore every place in the list.</p>
          <button type="button" onClick={() => setRetry((n) => n + 1)}>
            Try 3D again
          </button>
        </div>
      )}
      {ready && (
        <div className="be-gesture">
          Drag to rotate <span>·</span> Pinch or scroll to zoom
        </div>
      )}
      </div>

      <div className="be-map-bar">
      <div className="be-weather">
        <button
          type="button"
          className="be-weather-switch"
          role="switch"
          aria-checked={weatherEnabled}
          disabled={!ready}
          onClick={() => {
            const next = !weatherEnabled;
            controller.current?.weather(next);
            setWeatherEnabled(next);
          }}
        >
          <span>Weather {weatherEnabled ? 'on' : 'off'}</span>
          <span className="be-switch-track" aria-hidden="true">
            <span />
          </span>
        </button>
        {weatherEnabled ? (
          <div aria-live="polite">
            <strong>
              {weather.data
                ? `${Math.round(weather.data.temperature)}°C · ${weatherLabel(weather.data.code, weather.data.day)}`
                : weather.state === 'loading'
                  ? 'Checking Belvoir weather…'
                  : 'Weather unavailable'}
            </strong>
            <small>
              {weather.data
                ? `Belvoir · ${weatherTime} UK time`
                : weather.state === 'unavailable'
                  ? 'Showing an illustrated sky'
                  : 'Today at the castle'}{' '}
              ·{' '}
              <a href="https://open-meteo.com/" target="_blank" rel="noreferrer">
                Open-Meteo
              </a>
            </small>
          </div>
        ) : (
          <small>Original grey background</small>
        )}
      </div>

      <div className="be-duke-control">
        <button
          type="button"
          disabled={!ready || drive.busy}
          onClick={() => {
            if (controller.current?.dukeDrive()) setLiving(!reduced);
          }}
        >
          {drive.busy ? 'Duke’s drive…' : drive.away ? 'Bring the Duke home' : 'Send the Duke for a drive'}
        </button>
        <small role="status">{drive.message}</small>
      </div>

      <div className="be-life-control">
        <button
          type="button"
          disabled={!ready || reduced}
          aria-pressed={living && !reduced}
          onClick={() => setLiving(controller.current?.animate(!living) || false)}
        >
          {living && !reduced ? 'Pause activity' : 'Play activity'}
        </button>
      </div>
      </div>
    </>
  );
}
